import "./style.scss";

import CachedIcon from "@mui/icons-material/Cached";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Skeleton } from "@mui/material";
import DocumentationLink from "components/shared/DocumentationLink";
import StyledButton from "components/shared/StyledButton";
import { SetStateAction, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { IFetchSheetParams, IRowsData } from "store/partnerStatementsStore";
import { fetchGoogleSheetData } from "store/partnerStatementsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import { jaroWinkler } from "../../../../helpers/jaroWinkler";
import type { INewStatementForm } from "../../types";
import SheetDataByLine from "./SheetDataByLine";
import SheetRecapBtn from "./SheetRecap";
/**
 * This step is configuration of the statement template
 */
interface IStatementConfig {
    stepTitle: string;
    stepSubtitle: string;
    indexStep: number;
    /**
     * Params for fetchSheetData
     */
    sheetFetchParams: IFetchSheetParams;
    sheetName: string;
    spreadsheetName: string;
    mode: "new" | "edit";
    /**
     * Name of the template, used for edit mode
     */
    templateName: string | undefined;
    /**
     * Keys for all crealo variables
     */
    variablesKeys: string[];
    /**
     * Full name for variables
     */
    variablesNames: Record<string, string>;
    iterativeLineWarningRef: React.RefObject<HTMLDivElement>;
}

const StatementConfig = ({
    stepTitle,
    stepSubtitle,
    indexStep,
    sheetFetchParams,
    sheetName,
    spreadsheetName,
    mode,
    templateName,
    variablesKeys,
    variablesNames,
    iterativeLineWarningRef,
}: IStatementConfig) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { googleSheetData, fetchGoogleSheetDataStatus } = useAppSelector((state) => state.partnerStatements);
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext<INewStatementForm>();

    const iterationRange = watch("iterationRange");
    const integrationId = watch("integration")?.id;
    const variables = watch("variables");

    useEffect(() => {
        if (mode === "new") dispatch(fetchGoogleSheetData(sheetFetchParams));
    }, [dispatch]);

    const onGoogleSheetNavigate = () => {
        window.open(
            `https://docs.google.com/spreadsheets/d/${sheetFetchParams.spreadsheetId}/edit#gid=${sheetFetchParams.sheetId}`,
            "_blank",
        );
    };

    const onReloadVariables = () => {
        dispatch(fetchGoogleSheetData(sheetFetchParams));
    };
    /**
     * Object having has key variables and value, the most matching variable.
     * It's used to autocomplete fields with our matching variables. It can complete with a wrong one but the user is free to verify and change it.
     * jaroWinkler() verify matching distance between two words. 0 for no resemblance to 1 if it's the same exact word.
     * I choose the 0.9 distance matching.
     */
    const greatVarRange = useMemo(() => {
        const matchingVar: Record<string, string> = {};
        Object.keys(googleSheetData.varKeys).forEach((crealoVar) => {
            const cleanedCrealoVar = crealoVar.replace("crealo:", "");
            variablesKeys.forEach((key) => {
                const distance = jaroWinkler(cleanedCrealoVar, variablesNames[key]);
                if (distance >= 0.9) matchingVar[`${cleanedCrealoVar}`] = key;
            });
        });
        return matchingVar;
    }, [googleSheetData]);

    /**
     * Loop on the object created before to setValue() in the correspunding field. Only on creation statement mode.
     */
    useEffect(() => {
        if (mode === "new") {
            Object.keys(googleSheetData.varKeys).forEach((crealoVar) => {
                const cleanedCrealoVar = crealoVar.replace("crealo:", "");
                if (greatVarRange[`${cleanedCrealoVar}`]) {
                    setValue(`variables.${crealoVar}`, greatVarRange[`${cleanedCrealoVar}`]);
                }
            });
        }
    }, [googleSheetData, mode, variables]);

    return (
        <div className="new-statement-config">
            <h3 className="form-section-title">
                {indexStep + 1}. {stepTitle}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>
            <div className="form-fields">
                <SheetRecapBtn
                    sheetName={sheetName}
                    spreadsheetName={spreadsheetName}
                    onGoogleSheetNavigate={onGoogleSheetNavigate}
                    templateName={templateName}
                    mode={mode}
                />
                <div className="new-statement-config-content-infos">
                    <div className="sheet-data-display-infos">
                        {fetchGoogleSheetDataStatus === "pending" ? (
                            <Skeleton width={"10rem"} height={"2rem"} />
                        ) : (
                            <>
                                {googleSheetData.varCount > 0 && (
                                    <>
                                        <div className="crealo-var-found-number">
                                            <span>{`[${googleSheetData?.varCount}] variables`}</span>
                                            <span className="var-crealo">crealo:</span>
                                            <span>{t("PartnerStatements.Found")}.</span>
                                        </div>
                                        <div className="sheet-data-comprehensive-infos">
                                            <li>{t("PartnerStatements.SheetSectionInfo")}</li>
                                            <li>{t("PartnerStatements.SheetRowInfo")}</li>
                                        </div>
                                    </>
                                )}
                                {googleSheetData.varCount == 0 && mode === "new" && (
                                    <div className="google-sheet-no-data-found">
                                        <div className="crealo-var-found-number">
                                            <span>{t("PartnerStatements.NoVariablesFound")}</span>
                                            <span className="var-crealo">crealo:</span>
                                        </div>
                                        <DocumentationLink
                                            title={t("Documentation.CreateCustomStatement")}
                                            description={t("Documentation.ClickToReachDoc")}
                                            link=""
                                        />
                                    </div>
                                )}
                                {googleSheetData.varCount == 0 &&
                                    mode === "edit" &&
                                    (integrationId ? (
                                        <div className="google-sheet-no-data-found-desync">
                                            <span className="google-no-data">
                                                {t("PartnerStatements.IntegrationNoDataFound")}
                                            </span>
                                            <li>{t("PartnerStatements.SpreadsheetDeleted")}</li>
                                            <li>{t("PartnerStatements.SpreadsheetInexistant")}</li>
                                        </div>
                                    ) : (
                                        <div className="google-sheet-no-data-found-desync">
                                            <span className="google-is-desync">
                                                {t("PartnerStatements.GoogleIsDesync")}
                                            </span>
                                        </div>
                                    ))}
                            </>
                        )}
                    </div>
                </div>
                <div className="google-sheet-lines-wrapper">
                    {fetchGoogleSheetDataStatus === "pending" && (
                        <div>
                            <Skeleton width="60%" height="2rem" />
                            <Skeleton width="100%" height="2rem" />
                            <Skeleton width="100%" height="2rem" />
                        </div>
                    )}
                    {fetchGoogleSheetDataStatus !== "pending" && googleSheetData.varCount > 0 && (
                        <div>
                            <StyledButton
                                buttonType="outlined"
                                sx={{ marginBottom: "1rem" }}
                                onClick={onReloadVariables}
                            >
                                <CachedIcon sx={{ height: "18px", width: "18px" }} />
                                {t("PartnerStatements.RefreshVariables")}
                            </StyledButton>
                            {googleSheetData.varCount > 0 && !iterationRange && (
                                <div
                                    className={`choose-repeat-line-info ${errors?.iterationRange ? "warning" : ""}`}
                                    ref={iterativeLineWarningRef}
                                >
                                    <InfoOutlinedIcon sx={{ width: "16px", height: "16px" }} />
                                    <span>{t("PartnerStatements.IterativeLineRequired")}</span>
                                </div>
                            )}
                            <div className="google-sheet-lines-wrapper">
                                {googleSheetData?.rowsData.map((row: IRowsData) => {
                                    return (
                                        <SheetDataByLine
                                            variablesKeys={variablesKeys}
                                            variablesNames={variablesNames}
                                            lineNumber={row.line}
                                            crealoVariables={row.variables}
                                            lineRange={row.range}
                                            key={row.range}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {mode === "edit" && <br />}
        </div>
    );
};

export default StatementConfig;
